
import DetailComponentBase from "@/app/dynamic-components/details/details-components/detail-component.base";
import {
  DynamicDetailEntry,
  ExchangeDefinitionStep,
} from "@/app/dynamic-components/details/dynamic-detail.model";
import { DetailContext } from "@/app/contexts/detail.context";
import { Component, Vue } from "vue-property-decorator";
import OverviewComponent from "@/app/dynamic-components/overviews/overview-component.vue";
import ExchangeComponent from "@/app/components/exchange/exchange-component.vue";
import FormWrapper from "@/app/dynamic-components/forms/FormWrapper.vue";
import { DynamicForm } from "@/app/dynamic-components/forms/dynamic-form.model";
import { ExternalContext } from "@/app/contexts/externalContext";
import { dossierServiceV2 } from "@/app/services/dossier.service";
import { documentServiceV2 } from "@/app/services/document.service";
import { OverviewControl } from "@/app/dynamic-components/overviews/overiew.model";
import store from "@/store";

export type ListMetaData = {
  hidePills: boolean
  categoryId: string;
  translate: boolean;
  types: { [key: string]: string };
  readonlyTypes: { [key: string]: string };
  referenceType: string;
  referenceId: string;
  referenceParam: string;
  addNewDocumentLabel: string
  allowMultipleTypes: boolean
  subtitle: string
  description: string
  involvedParties: {
    portalId : string //reference to identify the reason the user was added as party
    roleId? : string //role of user/org to grant to
    contactId? : string //id of user/org to grant to
    contactType : ("USER_IN_CURRENT_ORGANISATION" |    "USER" |    "ORGANISATION" |    "ALL_USERS" |    "BCCA" |    "USERS_WITH_ROLE" |    "ORGANISATIONS_WITH_ROLE")
  }[]
};

@Component({
  components: { FormWrapper, ExchangeComponent, OverviewComponent },
})
export default class DetailComponentDocumentChecklist extends DetailComponentBase<ListMetaData> {
  public checklist: {
    label: string;
    id: string;
    present: boolean;
  }[] = [];
  path = `api/v2/documents/metadata`;
  additionalFilters: { [key: string]: string } = {};
  additionalMetadata: { [key: string]: any } = {};

  externalContext: ExternalContext = new ExternalContext();

  showAddFileDialog = false;
  savingDocument = false;
  formAdd: DynamicForm | undefined;

  showViewFileDialog = false;
  formView: DynamicForm | undefined;

  public addDocumentFormDefinition;
  public viewDocumentFormDefinition;

  async postEntityChanged(newValue: DynamicDetailEntry) {
    if (newValue) {
      if (!newValue.metadata.types) {
        newValue.metadata.types = {};
      }

      this.additionalFilters = {
        ae_documentcategory: this.metadata.categoryId
      };
      this.additionalFilters[this.metadata.referenceParam || 'nrq_regarding'] = this.metadata.referenceId ? this.metadata.referenceId : this.detailContext.resolveFieldValue("data.dossierId")

      this.checklist = [];
      const readonlyTypes: {
        label: string;
        id: string;
        present: boolean;
      }[] = [];
      const types: {
        label: string;
        id: string;
        present: boolean;
      }[] = [];
      if(newValue.metadata.readonlyTypes){
        Object.entries(newValue.metadata.readonlyTypes).forEach((entry) => {
          const e = { label: entry[1] as string, id: entry[0], present: false };
          this.checklist.push(e);
          readonlyTypes.push(e);
        });
      }
      if(newValue.metadata.types){
        Object.entries(newValue.metadata.types).forEach((entry) => {
          const e = { label: entry[1] as string, id: entry[0], present: false };
          this.checklist.push(e);
          types.push(e);
        });
      }
      this.addDocumentFormDefinition = this.generateAddDefinition(
        this.entry?.metadata?.categoryId,
        this.entry?.metadata?.allowMultipleTypes,
      );
      this.viewDocumentFormDefinition = this.generateViewDefinition(
        this.entry?.metadata?.categoryId,
        this.rules.canUploadFiles,
        this.entry?.metadata?.allowMultipleTypes
      );

      this.externalContext.setData("types", this.checklist);
      this.externalContext.setData("readonlytypes", readonlyTypes);
      this.externalContext.setData("editableTypes", types);
      this.externalContext.setData("categoryId", newValue.metadata.categoryId);
      this.externalContext.setData("referenceId", this.metadata.referenceId ? this.metadata.referenceId : this.detailContext.resolveFieldValue("data.dossierId"));
      this.externalContext.setData("translate", newValue.metadata.translate);
      this.externalContext.setData("dossier", this.detailContext.data.dossier);
      this.additionalMetadata.types = this.checklist;
      this.additionalMetadata.translated = newValue.metadata.translate;

      this.resolveChecklist();
    }
  }

  recieveFormAdd(form: DynamicForm) {
    this.formAdd = form;
  }

  recieveFormView(form: DynamicForm) {
    this.formView = form;
  }

  openViewFileDialog(event?: { item: any; index: number }) {
    this.showViewFileDialog = false;
    this.showAddFileDialog = false;

    this.showViewFileDialog = true;
    this.formView?.patchForm(event?.item);
  }
  openAddFileDialog() {
    this.showViewFileDialog = false;
    this.showAddFileDialog = false;

    this.showAddFileDialog = true;
    this.formAdd?.reset();
  }
  closeDialog() {
    this.showViewFileDialog = false;
    this.showAddFileDialog = false;
    this.formView?.reset();
    this.formAdd?.reset();
  }

  private _overviewControl: OverviewControl | undefined;
  recieveOverviewControl(overviewControl: OverviewControl) {
    this._overviewControl = overviewControl;
  }

  async deleteExistingDocument() {
    if (!this.formView) return;
    const item = this.formView?.formData;
    if (!item?.id) return;

    this.savingDocument = true;
    const success = await documentServiceV2.deleteDocumentById(item.id);
    if (success) {
      this.savingDocument = false;
      this.closeDialog();
      this._overviewControl?.refresh();
      this.resolveChecklist();
    }
    this.savingDocument = false;
  }

  async updateExistingDocument() {
    if(!this.formView) return;

    if (!this.formView) return;
    const item = this.formView?.formData;
    if (!item?.id) return;

    const success = await FormWrapper.SAVE_FORM_ASYNC(this.formView, () => this.savingDocument = true, () => this.savingDocument = false, async formData => {
      const metadata = await documentServiceV2.getDocumentMetadata(item.id[0]);
      metadata.description = item.description;
      metadata.type = item.type;
      const success = await documentServiceV2.updateDocumentMetadata(item.id, metadata);
      if(!success) return Promise.reject("Failed to update metadata");

      return {
        id: item.id[0],
        type: 'ae_document'
      }
    })
    if (success) {
      this.closeDialog();
      this._overviewControl?.refresh();
      this.resolveChecklist();
    }
  }

  async addNewDocument() {
    if (!this.formAdd) return;

    const success = await FormWrapper.SAVE_FORM_ASYNC(this.formAdd, () => this.savingDocument = true, () => this.savingDocument = false, async formData => {
      return {
        id: this.metadata.referenceId? this.metadata.referenceId : this.detailContext.resolveFieldValue("data.dossierId"),
        type: this.metadata.referenceType ? this.metadata.referenceType : "dossier"
      }
    })

    if(success){
      this._overviewControl?.refresh();
      this.resolveChecklist();
      this.closeDialog();
    }
  }

  private generateAddDefinition(categoryId: any, selectMultipleTypes: boolean) {
    return {
      id: "root",
      name: "",
      type: "LinearSection",
      displayMode: "editable",
      displayModeRule: "",
      computedValue: "",
      metadata: {},
      children: [
        {
          id: "file",
          name: "checklist.add_document_dialog.documents",
          type: "FileInput",
          displayMode: "inherit",
          displayModeRule: "",
          computedValue: "",
          validators: ["required()"],
          metadata: {
            multiple: true,
            categoryId: categoryId,
            typeIdsTemplate: "data.form.checklist",
            descriptionTemplate: "data.form.extraInfo",
            accept: ".xlsx,.docx,.pdf,.zip,.rar,.7z,.tar,.tar.gz,.tar.tgz,.png,.jpg,.jpeg,.mp4,.txt,.csv",
            involvedParties: this.metadata.involvedParties
          },
        },
        {
          id: "checklist",
          name: "checklist.add_document_dialog.document_type",
          type: "Selectv2",
          displayMode: "inherit",
          displayModeRule: "",
          computedValue: "",
          validators: ["required()"],
          metadata: {
            label: "checklist.add_document_dialog.document_type",
            items: "data.external.editableTypes",
            itemText: "label",
            itemValue: "id",
            multiple: selectMultipleTypes,
            translate: true,
          },
        },
        {
          id: "extraInfo",
          name: "checklist.add_document_dialog.extra_info",
          type: "TextArea",
          displayMode: "inherit",
          displayModeRule: "",
          computedValue: "",
          validators: ["maxLength(100)"],
          metadata: {},
        },
      ],
    };
  }
  private generateViewDefinition(categoryId: any, editable: boolean, selectMultipleTypes: boolean) {
    return {
      id: "root",
      name: "",
      type: "LinearSection",
      displayMode: editable ? "editable" : "readonly",
      displayModeRule: "",
      computedValue: "",
      metadata: {},
      children: [
        {
          id: "id",
          name: "checklist.view_document_dialog.documents",
          type: "FileInput",
          displayMode: "readonly",
          displayModeRule: "",
          computedValue: "",
          validators: ["required()"],
          metadata: {
            multiple: false,
            categoryId: categoryId,
            typeIdsTemplate: "data.form.checklist",
            descriptionTemplate: "data.form.extraInfo",
            accept: ".xlsx,.docx,.pdf,.zip,.rar,.7z,.tar,.tar.gz,.tar.tgz,.png,.jpg,.jpeg,.mp4,.txt,.csv",
            involvedParties: this.metadata.involvedParties
          },
        },
        {
          id: "uploadedBy",
          name: "",
          type: "LinearSection",
          displayMode: "readonly",
          displayModeRule: "",
          computedValue: "",
          validators: [],
          metadata: {
            SkipElementAsFormData: true,
            direction: "horizontal",
          },
          children: [
            {
              id: "uploadedBy",
              name: "",
              type: "LinearSection",
              displayMode: "inherit",
              displayModeRule: "",
              computedValue: "",
              validators: [],
              metadata: {},
              children: [
                {
                  id: "contactId",
                  name: "checklist.view_document_dialog.created_by",
                  type: "SelectPerson",
                  displayMode: "inherit",
                  displayModeRule: "",
                  computedValue: "",
                  validators: [],
                  metadata: {},
                },
              ],
            },
            {
              id: "createdOn",
              name: "checklist.view_document_dialog.created_on",
              type: "TextField",
              displayMode: "inherit",
              displayModeRule: "",
              computedValue: "",
              validators: [],
              metadata: {},
            },
          ],
        },
        {
          id: "type",
          name: "checklist.view_document_dialog.document_type",
          type: "Selectv2",
          displayMode: "readonly",
          displayModeRule: "",
          computedValue: "",
          validators: ["required()"],
          metadata: {
            label: "checklist.add_document_dialog.document_type",
            items: "data.external.types",
            itemText: "label",
            itemValue: "id",
            multiple: true,
            translate: true,
          },
        },
        {
          id: "description",
          name: "checklist.view_document_dialog.extra_info",
          type: "TextArea",
          displayMode: editable ? "editable" : "inherit",
          displayModeRule: "",
          computedValue: "",
          validators: ["maxLength(100)"],
          metadata: {},
        },
      ],
    };
  }

  private resolveChecklist() {
    if (!this.entry) return;
    documentServiceV2
      .getAllDocumentsByCategory(
        this.entry.metadata.categoryId,
          this.metadata.referenceId ? this.metadata.referenceId : this.detailContext.resolveFieldValue("data.dossierId"), this.metadata.referenceParam
      )
      .then((allDocs) => {
        const types = new Set();
        allDocs.forEach((value) => {
          if (value.type && value.type.length > 0) {
            value.type.forEach((type) => {
              types.add(type);
            });
          }
        });
        this.checklist.forEach(
          (value) => (value.present = types.has(value.id))
        );
      });
  }
}
